let activePlanningMutationCount = 0;
const activityMessages = new Map();

const pendingBackgroundWorkByKey = new Map();
const planningMutationActivityListeners = new Set();
let activePlanningUpdateCount = 0;
const planningLiveSyncListeners = new Set();
const deferredPlanningLiveSync = new Map();

export const PLANNING_UPDATE_BUSY_MESSAGE = "Mise à jour du planning en cours";

export function isPlanningWriteBusy() {
  return activePlanningMutationCount + activePlanningUpdateCount > 0;
}

export function getPlanningWriteActivityMessage() {
  return isPlanningWriteBusy() ? PLANNING_UPDATE_BUSY_MESSAGE : "";
}

export function tryBeginPlanningUpdateActivity() {
  return isPlanningWriteBusy() ? null : beginPlanningUpdateActivity();
}

// An update includes its final reads. It must not hold the write lock needed
// by metadata synchronization during those reads.
export function beginPlanningUpdateActivity() {
  activePlanningUpdateCount += 1;
  notifyPlanningLiveSyncActivity();
  let finished = false;
  return () => {
    if (finished) return;
    finished = true;
    activePlanningUpdateCount -= 1;
    notifyPlanningLiveSyncActivity();
  };
}

export function getActivePlanningUpdateCount() {
  return activePlanningUpdateCount;
}

export function subscribePlanningLiveSyncActivity(listener) {
  planningLiveSyncListeners.add(listener);
  return () => planningLiveSyncListeners.delete(listener);
}

export function cancelDeferredPlanningLiveSync(key) {
  deferredPlanningLiveSync.delete(key);
}

export function deferPlanningLiveSyncUntilIdle(key, work) {
  if (activePlanningMutationCount + activePlanningUpdateCount === 0) return false;
  deferredPlanningLiveSync.set(key, work);
  return true;
}

function notifyPlanningLiveSyncActivity() {
  const isActive = activePlanningMutationCount + activePlanningUpdateCount > 0;
  planningLiveSyncListeners.forEach((listener) => listener({ isActive }));
  if (isActive) return;
  for (const [key, work] of deferredPlanningLiveSync) {
    if (activePlanningMutationCount + activePlanningUpdateCount > 0) break;
    deferredPlanningLiveSync.delete(key);
    try { work(); } catch (error) { queueMicrotask(() => { throw error; }); }
  }
}

function buildPlanningMutationActivitySnapshot() {
  return {
    activeCount: activePlanningMutationCount,
    isActive: activePlanningMutationCount > 0,
  };
}

function notifyPlanningMutationActivityListeners() {
  const snapshot = buildPlanningMutationActivitySnapshot();
  planningMutationActivityListeners.forEach((listener) => {
    listener(snapshot);
  });
  notifyPlanningLiveSyncActivity();
}

function flushDeferredPlanningBackgroundWork() {
  if (activePlanningMutationCount > 0 || pendingBackgroundWorkByKey.size === 0) {
    return;
  }

  const pendingEntries = [...pendingBackgroundWorkByKey.entries()];
  pendingBackgroundWorkByKey.clear();

  for (let index = 0; index < pendingEntries.length; index += 1) {
    if (activePlanningMutationCount > 0) {
      pendingEntries.slice(index).forEach(([key, work]) => {
        if (!pendingBackgroundWorkByKey.has(key)) {
          pendingBackgroundWorkByKey.set(key, work);
        }
      });
      return;
    }

    const [, work] = pendingEntries[index];
    try {
      work();
    } catch (error) {
      queueMicrotask(() => {
        throw error;
      });
    }
  }
}

export function beginPlanningMutationActivity({ message = "" } = {}) {
  const token = Symbol("planning-mutation");
  if (message) activityMessages.set(token, message);
  activePlanningMutationCount += 1;
  notifyPlanningMutationActivityListeners();

  let didFinish = false;
  return function finishPlanningMutationActivity() {
    if (didFinish) {
      return;
    }
    didFinish = true;
    activityMessages.delete(token);
    activePlanningMutationCount = Math.max(0, activePlanningMutationCount - 1);
    notifyPlanningMutationActivityListeners();
    flushDeferredPlanningBackgroundWork();
  };
}

export function tryBeginPlanningMutationActivity({ allowDuringUpdate = false, ...options } = {}) {
  if (activePlanningMutationCount > 0 || (!allowDuringUpdate && activePlanningUpdateCount > 0)) {
    return null;
  }
  return beginPlanningMutationActivity(options);
}

export function cancelDeferredPlanningBackgroundWork(key) {
  pendingBackgroundWorkByKey.delete(key);
}

export function deferPlanningBackgroundWorkUntilIdle(key, work) {
  if (activePlanningMutationCount <= 0) {
    return false;
  }
  if ((typeof key !== "string" && typeof key !== "symbol") || typeof work !== "function") {
    throw new TypeError("Une clé et une fonction sont requises pour différer un rafraîchissement planning.");
  }

  pendingBackgroundWorkByKey.set(key, work);
  return true;
}

export function getActivePlanningMutationCount() {
  return activePlanningMutationCount;
}

export function subscribePlanningMutationActivity(listener) {
  if (typeof listener !== "function") {
    return () => {};
  }
  planningMutationActivityListeners.add(listener);
  return () => planningMutationActivityListeners.delete(listener);
}

export function getPlanningMutationActivityMessage() {
  return [...activityMessages.values()].at(-1) || "";
}
